/* istanbul ignore file */
/* eslint-disable max-lines */
import {
  Account,
  Avatar,
  AvatarAction,
  AvatarEmotion,
  AvatarGesture,
  AvatarModelType,
  ChoiceUserInputAction,
  ChoiceUserInputBranch,
  ChoiceUserInputBranchState,
  Project,
  DetailedSessionFeedback,
  Student,
  EducationSessionRecordState,
  EducationSessionResult,
  Gender,
  LanguageCode,
  Challenge,
  ChallengeMode,
  ChallengeBadWordsFilter,
  ChallengeDisplayMode,
  ChallengeRecognitionAccuracy,
  ChallengeRecognitionEngine,
  ChallengeInputMode,
  ChallengeScenarioAfterAction,
  SystemAction,
  UserInputAction,
  ActionResult,
  ChallengeScenarioActivationMethod,
  Admin,
  AnonymousResult,
  ChallengeScenarioAfterActionTarget,
  EducationSessionPassedResult,
  CompetenceAnalysisState,
  DisplayParametricResultMode,
  ResultDescription,
  CompetenceResult,
  ParametricResultDescription,
  ParametricResultCondition,
  ParametricResult,
  CustomParameter,
} from "./schema";
import {createMemoryHistory} from "history";
import {Scenario} from "./types";

function randomId(prefix = "") {
  return prefix + Math.floor(Math.random() * 100000).toString();
}

type createDummyHandler<T> = (params?: Partial<T>) => T;

export const createDummyAccount: createDummyHandler<Account> = function (params = {}) {
  return {
    id: randomId("a"),
    __typename: "Account",
    name: "Account",
    code: "account1",
    allowRetries: false,
    attemptsRemains: 100,
    hasActiveMembership: true,
    membershipType: "Admin",
    studentsLimit: 100,
    textLogo: null,
    receiveReports: false,
    ...params,
  };
};

export const createDummyAdmin: createDummyHandler<Admin> = function (params = {}) {
  return {
    id: randomId("u"),
    __typename: "Admin",
    name: "Admin",
    email: "admin@dailo.co",
    extraEmail: "admin@dailo.co",
    fullName: "Admin",
    isDeveloper: false,
    isExperimental: false,
    experimentalMode: false,
    accountsCount: 1,
    isStaff: false,
    hasActiveMembership: true,
    groups: [],
    top3Emotions: [],
    ...params,
  };
};

export const createDummyStudent: createDummyHandler<Student> = function (params = {}) {
  return {
    id: randomId("u"),
    __typename: "Student",
    name: "Student",
    email: "student@dailo.co",
    extraEmail: "editor2@dailo.co",
    fullName: "Student",
    isDeveloper: false,
    isExperimental: false,
    experimentalMode: false,
    accountsCount: 1,
    isStaff: false,
    hasActiveMembership: true,
    groups: [],
    top3Emotions: [],
    ...params,
  };
};

export const createDummyAvatar: createDummyHandler<Avatar> = function (params = {}) {
  return {
    id: randomId("avatar"),
    __typename: "Avatar",
    name: "Avatar",
    modelType: AvatarModelType.UNKNOWN,
    gender: Gender.MALE,
    previewFull: "",
    preview: "",
    gltfAsset: "",

    ...params,
  };
};

export const createDummyChallenge: createDummyHandler<Challenge> = function (params = {}) {
  return {
    id: randomId("l"),
    accessToken: "secret",
    __typename: "Challenge",
    mode: ChallengeMode.COMPETENCE_TEST,
    displayMode: ChallengeDisplayMode.DEFAULT,
    inputMode: ChallengeInputMode.VOICE,
    name: "Challenge 1",
    badWordsFilter: ChallengeBadWordsFilter.NO,
    avatar: createDummyAvatar(),
    gestures: [],
    background: null,
    canPlay: true,
    hasPassed: false,
    languageCode: LanguageCode.EN_US,
    recognitionEngine: ChallengeRecognitionEngine.IDENTIFY,
    recognitionAccuracy: ChallengeRecognitionAccuracy.NORMAL,
    customParameters: [],
    generateVideo: false,
    createdAt: Date.now() / 1000,
    lastUpdatedAt: Date.now() / 1000,
    accountId: null,
    participantsExportDownloadUrl: "[participantsExportDownloadUrl]",
    scormDownloadUrl: "[scormDownloadUrl]",
    displayParametricResultMode: DisplayParametricResultMode.RAW_SUM,

    ...params,
  };
};

export const createDummyProject: createDummyHandler<Project> = function (params = {}) {
  return {
    id: randomId("c"),
    accessToken: "secret",
    __typename: "Project",
    name: "Project 1",
    languageCode: LanguageCode.EN_US,
    createdAt: Date.now() / 1000,
    lastUpdatedAt: Date.now() / 1000,
    challenges: [],
    canPlay: true,
    hasPassed: false,
    ...params,
  };
};

export const createDummyScenario: createDummyHandler<Scenario> = function (params = {}) {
  return {
    id: randomId("sc"),
    __typename: "Scenario",
    name: "Scenario 1",
    actions: [],
    isMain: true,
    index: 1,
    nextScenario: null,
    nextScenarioIndex: 1,
    afterActionTarget: ChallengeScenarioAfterActionTarget.MAIN,
    triggerPhrases: [],
    afterAction: ChallengeScenarioAfterAction.RETURN,
    activationMethod: ChallengeScenarioActivationMethod.TRIGGER_PHRASE,
    ...params,
  };
};

export const createDummyAvatarAction: createDummyHandler<AvatarAction> = function (params = {}) {
  return {
    id: randomId("aa"),
    __typename: "AvatarAction",
    text: "[text]",
    emotion: AvatarEmotion.IDLE,
    gesture: AvatarGesture.IDLE,
    audio: "",
    isCustomAudio: false,
    setMediaAsBackground: false,

    ...params,
  };
};

export const createDummySystemAction: createDummyHandler<SystemAction> = function (params = {}) {
  return {
    id: randomId("sa"),
    __typename: "SystemAction",
    text: "[text]",
    audio: "",
    isCustomAudio: false,
    media: "",
    mediaType: "",
    mediaViewRequired: false,
    setMediaAsBackground: false,
    replaceCharacterWithMedia: false,

    ...params,
  };
};

export const createDummyUserInputAction: createDummyHandler<UserInputAction> = function (params = {}) {
  return {
    id: randomId("uia"),
    __typename: "UserInputAction",
    freeSpeech: false,
    referenceText: "[referenceText]",
    questionText: "[questionText]",
    hintText: "",
    analogTexts: [],

    ...params,
  };
};

export const createDummyChoiceUserInputAction: createDummyHandler<ChoiceUserInputAction> = function (params = {}) {
  return {
    id: randomId("cuia"),
    __typename: "ChoiceUserInputAction",
    branches: [],
    questionText: "[questionText]",
    hintText: "",
    isMultipleChoice: false,
    competence: null,
    ...params,
  };
};

export const createDummyChoiceUserInputBranch: createDummyHandler<ChoiceUserInputBranch> = function (params = {}) {
  return {
    id: randomId("cuia"),
    __typename: "ChoiceUserInputBranch",
    state: ChoiceUserInputBranchState.CORRECT,
    userInput: createDummyUserInputAction(),
    avatarReaction: null,
    systemReaction: null,

    ...params,
  };
};

export const createDummyEducationSessionPassedResult: createDummyHandler<EducationSessionPassedResult> = (params) => {
  return {
    __typename: "EducationSessionPassedResult",
    passed: true,
    interrupt: false,
    recognizedText: "test text",
    ...params,
  };
};

export const createDummyEducationSessionResult: createDummyHandler<EducationSessionResult> = (params) => {
  return {
    sessionId: "0000-0000-0000-0000",
    __typename: "EducationSessionResult",
    record: null,
    recordState: EducationSessionRecordState.NOT_STARTED,
    passed: true,
    requestFeedback: false,
    score: 100,
    time: 300,
    usedHints: 0,
    actionResults: [],
    isContentMatches: true,
    finishedScenariosIds: [],
    parametricResults: [],
    detailedFeedback: createDummyDetailedSessionFeedback(),
    competenceAnalysisState: CompetenceAnalysisState.READY,
    competenceResults: [],
    isUserInputAnalysisReady: null,
    generalEmotionAnger: null,
    generalEmotionDisgust: null,
    generalEmotionEnthusiasm: null,
    generalEmotionFear: null,
    generalEmotionGuilt: null,
    generalEmotionJoy: null,
    generalEmotionNeutral: null,
    generalEmotionSadness: null,
    generalEmotionShame: null,
    generalEmotionSurprise: null,
    generalSentimentNeutral: null,
    generalSentimentPositive: null,
    generalSentimentNegative: null,
    ...params,
  };
};

export const createDummyActionResult: createDummyHandler<ActionResult> = function (params = {}) {
  return {
    __typename: "ActionResult",
    actionId: randomId("a"),
    branchId: randomId("b"),
    branchIds: [randomId("b")],
    duration: 1,
    attempts: 1,
    isSkipped: false,
    usedHint: false,
    passed: true,
    detailedAttempts: {
      passed: 0,
      failed: 0,
    },
    ...params,
  };
};

export const createDummyDetailedSessionFeedback: createDummyHandler<DetailedSessionFeedback> = (params) => ({
  __typename: "DetailedSessionFeedback",
});

export function createDummyHistory() {
  const history = createMemoryHistory();

  history.push("/");
  return history;
}

//@ts-ignore
export const createDummyAnonymousSession: createDummyHandler<AnonymousResult> = function (params = {}) {
  return {
    uuid: randomId("a"),
    challenge: createDummyChallenge(),
    project: "Новый проект",
    passed: true,
    score: 1000,
    OS: "MacOS Macintosh X 10.15.7",
    feedbackScore: 5,
    feedbackText: "",
    time: 10,
    record: "",
    parametricResults: [
      {
        parameterName: "Name",
        score: 80,
        rawSum: 80,
        percentage: 0.8,
      },
    ],
    actionResults: createDummyActionResult(),
    isContentMatches: true,
    finishedScenariosIds: [randomId("b"), randomId("c")],
    recordState: EducationSessionRecordState.READY,
    startedAt: 1743586387,
    finishedAt: Date.now() / 1000,
    ...params,
  };
};

export const createDummyResultDescription: createDummyHandler<ResultDescription> = function (params = {}) {
  return {
    id: randomId("rd"),
    minScore: 0,
    maxScore: 0,
    ...params,
  };
};

export const createDummyCompetenceResult: createDummyHandler<CompetenceResult> = function (params = {}) {
  return {
    competence: "Competence",
    score: 0,
    feedback: "Feedback",
    ...params,
  };
};

export const createDummyParametricResult: createDummyHandler<ParametricResult> = function (params = {}) {
  return {
    parameterName: "Parameter",
    rawSum: 0,
    percentage: 0,
    ...params,
  };
};

export const createDummyParametricResultCondition: createDummyHandler<ParametricResultCondition> = function (
  params = {},
) {
  return {
    parameter: createDummyCustomParameter(),
    competence: null,
    minValue: 0,
    maxValue: 0,
    ...params,
  };
};

export const createDummyParametricResultDescription: createDummyHandler<ParametricResultDescription> = function (
  params = {},
) {
  return {
    id: randomId("prd"),
    conditions: [],
    message: "Message",
    ...params,
  };
};

export const createDummyCustomParameter: createDummyHandler<CustomParameter> = function (params = {}) {
  return {
    id: randomId("cp"),
    name: "Parameter",
    ...params,
  };
};
